/**
 * IntraMonthTriggerPage
 *
 * DASH-RUN-001-V1A Phase 6A: Admin UI trigger page.
 * Platform Admin only — lets a VeraScore admin execute an intra-month
 * score run for a chosen member + month, in evidence-only mode.
 *
 * Safety: the backend runs in evidence_only mode (writes only to
 * algo_runs + intra_month_run_audit, never to verascores). A per-request
 * dry_run flag (reduce-only) lets the admin validate without executing the
 * pipeline; it can only add safety, never escalate to a verascores write.
 *
 * Access gate (client mirror of server): 'Platform Admin' role only.
 * Server guard is authoritative — client gate is UX only, not security.
 */

import { FC, useMemo, useRef, useState } from 'react';
import { FiPlay, FiInfo, FiCheckCircle, FiAlertTriangle, FiSearch } from 'react-icons/fi';
import { useUser } from '../context/UserContext';

// ─── Integrations API helper ─────────────────────────────────────────────────
const INTEGRATIONS_API = import.meta.env.VITE_INTEGRATIONS_API_URL || 'https://vs-integrations-development.netlify.app';
const API_BASE = import.meta.env.VITE_API_URL || '/api';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;

/** admin_reason enum accepted by POST /verascore/intra-month-score */
const ADMIN_REASONS = [
  { value: 'VALIDATION_CHECK', label: 'Validation check' },
  { value: 'NEW_FI_CONNECTED', label: 'New FI connected' },
  { value: 'BUG_FIX_VALIDATION', label: 'Bug-fix validation' },
  { value: 'SUPPORT_REQUEST', label: 'Support request' },
] as const;

type AdminReason = typeof ADMIN_REASONS[number]['value'];

interface IntraMonthResult {
  status: 'success' | 'dry_run';
  message: string;
  run_id?: string;
  member_id?: string;
  target_month?: string;
  final_score?: number;
  rating?: string;
  account_count?: number;
  algorithm_version?: string;
  prior_display_score?: { score: number; rating: string; source: string; officialized_at: string | null };
}

/**
 * Fetch the user's Auth0 access_token from api-verascore-platform.
 * Cached in sessionStorage to avoid repeated round-trips.
 */
const getSessionToken = async (forceRefresh = false): Promise<string> => {
  if (!forceRefresh) {
    const cached = sessionStorage.getItem('vs_admin_token');
    if (cached) return cached;
  }
  const res = await fetch(`${API_BASE}/verascore/session-token`, {
    method: 'GET',
    headers: { 'VS-Client-Id': VS_CLIENT_ID, 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`Failed to obtain session token (${res.status})`);
  const json = await res.json();
  // Platform wraps responses via ApiResponseHandler.success: { status, data: {...} }.
  // Read the token from data.* first, then fall back to any unwrapped shape.
  const token =
    json?.data?.access_token || json?.data?.token ||
    json?.access_token || json?.token;
  if (!token) throw new Error('Session-token endpoint returned no token');
  sessionStorage.setItem('vs_admin_token', token);
  return token;
};

/** POST /verascore/intra-month-score with Bearer auth + one 401/403 retry.
 * Served by the verascore.ts RouteMap dispatcher (last-path-segment match);
 * there is no /admin/* redirect or admin.ts function on the integrations site. */
const callIntraMonthApi = async (body: Record<string, unknown>): Promise<IntraMonthResult> => {
  const doFetch = async (token: string) =>
    fetch(`${INTEGRATIONS_API}/verascore/intra-month-score`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'VS-Client-Id': VS_CLIENT_ID,
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

  let token = await getSessionToken();
  let res = await doFetch(token);
  if (res.status === 401 || res.status === 403) {
    sessionStorage.removeItem('vs_admin_token');
    token = await getSessionToken(true);
    res = await doFetch(token);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${text}`);
  }
  // BUG-16: the platform wraps successful responses via ApiResponseHandler.success
  // as { status, data: {...} }. The result fields (run_id, final_score, etc.) live
  // under .data; returning the raw envelope left run_id undefined/mismatched in the
  // UI. Unwrap .data (falling back to the raw body for un-enveloped responses).
  const json = await res.json();
  return (json?.data ?? json) as IntraMonthResult;
};

/** Strict UUIDv4 — mirrors the backend validator (intraMonthValidator.ts). */
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** A member row as returned by GET /admin/members/search. */
interface MemberMatch {
  uuid: string;
  member_id: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  lifecycle_status?: string | null;
}

/**
 * Resolve a free-text identifier to member matches via the platform admin
 * search endpoint. Same-origin (api-verascore-platform) with cookie session.
 * The endpoint currently resolves email and member_id/uuid; name and auth0 id
 * are backend gaps (will return no match until the handler adds those branches).
 */
const searchMembers = async (q: string): Promise<MemberMatch[]> => {
  const res = await fetch(`${API_BASE}/verascore/admin/members/search?q=${encodeURIComponent(q)}`, {
    method: 'GET',
    headers: { 'VS-Client-Id': VS_CLIENT_ID, 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Member search failed (${res.status}): ${text}`);
  }
  const json = await res.json();
  const rows = Array.isArray(json) ? json : (json?.data ?? json?.results ?? []);
  return rows as MemberMatch[];
};

const memberLabel = (m: MemberMatch): string => {
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ').trim();
  return [name || null, m.email || null, m.member_id || null]
    .filter(Boolean)
    .join(' • ') || m.uuid;
};

// ─── Month options (current month back 24 months) ────────────────────────────
function monthOptions(): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 25; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const base = d.toLocaleString('default', { month: 'long', year: 'numeric' });
    // BUG-18: flag the current (still-open) month so it isn't mistaken for a
    // closed period. The default selection points at the most recent closed
    // month, not this one.
    const label = i === 0 ? `${base} (current — open)` : base;
    out.push({ value, label });
  }
  return out;
}

// ─── Presentational view ──────────────────────────────────────────────────────
interface ViewProps {
  activePersonaEmail: string;
  months: { value: string; label: string }[];
  identifier: string;
  setIdentifier: (v: string) => void;
  resolvedMember: MemberMatch | null;
  matches: MemberMatch[];
  resolving: boolean;
  resolveError: string | null;
  onResolve: () => void;
  onPick: (m: MemberMatch) => void;
  targetMonth: string;
  setTargetMonth: (v: string) => void;
  adminReason: AdminReason;
  setAdminReason: (v: AdminReason) => void;
  dryRun: boolean;
  setDryRun: (v: boolean) => void;
  canRun: boolean;
  loading: boolean;
  error: string | null;
  result: IntraMonthResult | null;
  onRun: () => void;
}

const inputClass =
  'w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500';

const IntraMonthTriggerView: FC<ViewProps> = ({
  activePersonaEmail, months, identifier, setIdentifier, resolvedMember, matches,
  resolving, resolveError, onResolve, onPick, targetMonth, setTargetMonth,
  adminReason, setAdminReason, dryRun, setDryRun, canRun, loading, error, result, onRun,
}) => (
  <div className="max-w-3xl mx-auto px-4 py-6">
    {/* Header */}
    <div className="mb-6">
      <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
        <FiPlay className="text-brand-blue" /> Intra-Month Score Trigger
      </h1>
      <p className="text-sm text-slate-500 mt-1">DASH-RUN-001-V1A • Evidence-only recompute</p>
    </div>

    {/* Evidence-only / dry-run context banner */}
    <div className="mb-6 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
      <FiInfo size={16} className="mt-0.5 shrink-0" />
      <div>
        Runs execute in <strong>evidence-only</strong> mode — results are written to the audit tables
        (<code>algo_runs</code>, <code>intra_month_run_audit</code>) and <strong>never</strong> to the
        member-facing <code>verascores</code> table. Tick <strong>Dry-run first</strong> below to
        validate without executing the pipeline (no writes at all).
      </div>
    </div>

    {/* Form */}
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
      <div>
        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Member</label>
        <div className="flex gap-2">
          <input
            className={inputClass}
            placeholder="UUID, email, member ID, name, or auth0|…"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onResolve(); } }}
          />
          <button
            type="button"
            onClick={onResolve}
            disabled={resolving || identifier.trim().length === 0}
            className="flex items-center gap-1 shrink-0 px-3 py-2 bg-slate-100 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FiSearch size={14} /> {resolving ? 'Resolving…' : 'Resolve'}
          </button>
        </div>
        <p className="mt-1 text-xs text-slate-400">
          Enter a UUID to use directly, or search by email / member ID / name / Auth0 ID.
        </p>

        {resolveError && (
          <p className="mt-1 text-xs text-red-500">{resolveError}</p>
        )}

        {/* Multiple matches — pick one */}
        {matches.length > 1 && !resolvedMember && (
          <ul className="mt-2 max-h-56 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100">
            {matches.map((m) => (
              <li key={m.uuid}>
                <button
                  type="button"
                  onClick={() => onPick(m)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                >
                  <span className="font-medium text-slate-700">{memberLabel(m)}</span>
                  <span className="block font-mono text-[11px] text-slate-400">{m.uuid}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Resolved member chip */}
        {resolvedMember && (
          <div className="mt-2 flex items-start gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
            <FiCheckCircle size={14} className="mt-0.5 shrink-0" />
            <div>
              <span className="font-medium">{memberLabel(resolvedMember)}</span>
              <span className="block font-mono text-[11px] text-green-700">{resolvedMember.uuid}</span>
            </div>
          </div>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Target month</label>
        <select className={inputClass} value={targetMonth} onChange={(e) => setTargetMonth(e.target.value)}>
          {months.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Reason</label>
        <select className={inputClass} value={adminReason} onChange={(e) => setAdminReason(e.target.value as AdminReason)}>
          {ADMIN_REASONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Admin actor</label>
        <input className={`${inputClass} bg-slate-50 text-slate-500`} value={activePersonaEmail} readOnly />
        <p className="mt-1 text-xs text-slate-400">Must match your authenticated email (server-enforced).</p>
      </div>

      <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={dryRun}
          onChange={(e) => setDryRun(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-blue focus:ring-blue-500"
        />
        <span>
          <span className="font-medium text-slate-700">Dry-run first</span> — validate the run
          (member, month, rate-limit) without executing the scoring pipeline. No writes at all.
        </span>
      </label>

      <button
        onClick={onRun}
        disabled={!canRun}
        className="flex items-center gap-2 px-4 py-2 bg-brand-blue text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <FiPlay size={14} /> {loading ? (dryRun ? 'Checking…' : 'Running…') : (dryRun ? 'Dry-run' : 'Run Score')}
      </button>
    </div>

    {/* Error */}
    {error && (
      <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        <FiAlertTriangle size={16} className="mt-0.5 shrink-0" />
        <span className="break-all">{error}</span>
      </div>
    )}

    {/* Result */}
    {result && (
      <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-5">
        <div className="flex items-center gap-2 mb-3">
          <FiCheckCircle className="text-green-600" size={18} />
          <span className="font-semibold text-green-800">
            {result.status === 'dry_run' ? 'Dry-run (no writes)' : 'Run complete'}
          </span>
        </div>
        <p className="text-sm text-green-800 mb-3">{result.message}</p>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          {result.final_score != null && (<><dt className="text-slate-500">Final score</dt><dd className="font-semibold">{result.final_score} {result.rating ? `(${result.rating})` : ''}</dd></>)}
          {result.prior_display_score && (<><dt className="text-slate-500">Prior display score</dt><dd>{result.prior_display_score.score} ({result.prior_display_score.rating})</dd></>)}
          {result.account_count != null && (<><dt className="text-slate-500">Accounts</dt><dd>{result.account_count}</dd></>)}
          {result.algorithm_version && (<><dt className="text-slate-500">Algorithm version</dt><dd className="font-mono text-xs">{result.algorithm_version}</dd></>)}
          {result.run_id && (<><dt className="text-slate-500">Run ID</dt><dd className="font-mono text-xs break-all">{result.run_id}</dd></>)}
        </dl>
      </div>
    )}
  </div>
);

const IntraMonthTriggerPage: FC = () => {
  const { activePersona } = useUser();
  const months = useMemo(monthOptions, []);

  // Free-text identifier the admin types (UUID / email / member_id / name / auth0 id).
  const [identifier, setIdentifier] = useState('');
  // The member UUID resolved from the identifier — this is what the backend needs.
  const [resolvedMember, setResolvedMember] = useState<MemberMatch | null>(null);
  const [matches, setMatches] = useState<MemberMatch[]>([]);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  // Monotonic token: incremented whenever the identifier changes so an in-flight
  // searchMembers() from a prior identifier cannot resolve a stale member.
  const resolveSeq = useRef(0);

  // BUG-18: default to the most recent CLOSED month (index 1), not the current
  // still-open month (index 0), so an admin doesn't unintentionally run against
  // an incomplete period. Falls back to index 0 only if no prior month exists.
  const [targetMonth, setTargetMonth] = useState(months[1]?.value ?? months[0]?.value ?? '');
  const [adminReason, setAdminReason] = useState<AdminReason>('VALIDATION_CHECK');
  const [dryRun, setDryRun] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IntraMonthResult | null>(null);

  const canRun = !!resolvedMember && UUID_V4.test(resolvedMember.uuid) && !!targetMonth && !loading;

  // Typing a new identifier clears any prior resolution and invalidates any
  // in-flight lookup (so a slow searchMembers() cannot resolve a stale member).
  const handleSetIdentifier = (v: string) => {
    resolveSeq.current += 1;
    setIdentifier(v);
    setResolvedMember(null);
    setMatches([]);
    setResolveError(null);
  };

  const handleResolve = async () => {
    const q = identifier.trim();
    if (!q) return;
    const seq = ++resolveSeq.current;
    setResolving(true);
    setResolveError(null);
    setResolvedMember(null);
    setMatches([]);
    try {
      // A raw UUID is used directly — no lookup needed.
      if (UUID_V4.test(q)) {
        if (seq !== resolveSeq.current) return; // identifier changed mid-resolve
        setResolvedMember({ uuid: q, member_id: null, email: null, first_name: null, last_name: null });
        return;
      }
      const found = await searchMembers(q);
      // Discard a stale result: the admin changed the identifier while this
      // lookup was in flight, so applying found[] would mismatch the input.
      if (seq !== resolveSeq.current) return;
      if (found.length === 0) {
        setResolveError('No member matched that identifier. Note: name and Auth0 ID lookup are not yet supported server-side.');
      } else if (found.length === 1) {
        setResolvedMember(found[0]);
      } else {
        setMatches(found);
      }
    } catch (err: any) {
      if (seq !== resolveSeq.current) return; // stale error — ignore
      setResolveError(err.message);
    } finally {
      if (seq === resolveSeq.current) setResolving(false);
    }
  };

  const handlePick = (m: MemberMatch) => {
    resolveSeq.current += 1; // an explicit pick supersedes any in-flight lookup
    setResolvedMember(m);
    setMatches([]);
  };

  const handleRun = async () => {
    if (!resolvedMember) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await callIntraMonthApi({
        member_id: resolvedMember.uuid,
        target_month: targetMonth,
        admin_actor: activePersona.email,
        admin_reason: adminReason,
        dry_run: dryRun,
      });
      setResult(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <IntraMonthTriggerView
      activePersonaEmail={activePersona.email}
      months={months}
      identifier={identifier}
      setIdentifier={handleSetIdentifier}
      resolvedMember={resolvedMember}
      matches={matches}
      resolving={resolving}
      resolveError={resolveError}
      onResolve={handleResolve}
      onPick={handlePick}
      targetMonth={targetMonth}
      setTargetMonth={setTargetMonth}
      adminReason={adminReason}
      setAdminReason={setAdminReason}
      dryRun={dryRun}
      setDryRun={setDryRun}
      canRun={canRun}
      loading={loading}
      error={error}
      result={result}
      onRun={handleRun}
    />
  );
};

export default IntraMonthTriggerPage;
